import React from 'react';
import PropTypes from 'prop-types';
import { FaPen, FaTrash } from 'react-icons/fa';
import CustomRuleEditor from './CustomRuleEditor';
import { build_rule_summary, can_be_instant } from './gen_config_utils';

// One custom generation rule: the summary line (which is also the name the
// generated checklists carry), an enable toggle, and edit / delete actions.
// While editing, the editor expands inline below the summary.
function CustomRuleCard({ rule, meta, saving, editing, onChange, onDelete, onToggleEdit }) {
  const summary = build_rule_summary(rule, meta);
  return (
    <div
      className="rounded-lg border border-line bg-surface-sunken p-3"
      style={{ borderLeft: `3px solid ${rule.enabled ? '#7c3aed' : '#9ca3af'}` }}
    >
      <div className="flex items-center gap-3">
        <label
          className="inline-flex items-center gap-1.5 text-xs cursor-pointer select-none"
          style={{ color: 'var(--text-muted)', marginBottom: 0, flexShrink: 0 }}
          title="Enabled rules run in the daily generation job"
        >
          <input
            type="checkbox"
            checked={rule.enabled}
            onChange={() => onChange({ ...rule, enabled: !rule.enabled })}
            disabled={saving}
          />
          Active
        </label>
        <span
          className="flex-1 text-sm font-medium"
          style={{ color: rule.enabled ? 'var(--text-primary)' : 'var(--text-muted)' }}
        >
          {summary}
        </span>
        {rule.is_instant && can_be_instant(rule.condition, meta) ? (
          <span
            className="inline-block px-2 py-0.5 rounded-full text-xs font-bold tracking-wide bg-state-warning-bg text-state-warning-text whitespace-nowrap"
            title="Created in real time from the membership event and alerted to retention"
          >
            INSTANT
          </span>
        ) : null}
        <div
          role="button"
          tabIndex={0}
          aria-label="Edit rule"
          onClick={() => !saving && onToggleEdit()}
          onKeyDown={(e) => {
            if ((e.key === 'Enter' || e.key === ' ') && !saving) {
              e.preventDefault();
              onToggleEdit();
            }
          }}
          className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium cursor-pointer hover:bg-surface-sunken ${
            editing ? 'bg-state-info-bg text-state-info-text' : 'text-content-secondary'
          }`}
        >
          <FaPen size={10} /> {editing ? 'Close' : 'Edit'}
        </div>
        <div
          role="button"
          tabIndex={0}
          aria-label="Delete rule"
          onClick={() => !saving && onDelete()}
          onKeyDown={(e) => {
            if ((e.key === 'Enter' || e.key === ' ') && !saving) {
              e.preventDefault();
              onDelete();
            }
          }}
          className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium text-state-error cursor-pointer hover:bg-state-error-bg"
        >
          <FaTrash size={10} /> Delete
        </div>
      </div>
      {editing ? (
        <CustomRuleEditor rule={rule} meta={meta} saving={saving} onChange={onChange} />
      ) : null}
    </div>
  );
}

CustomRuleCard.propTypes = {
  rule: PropTypes.shape({
    id: PropTypes.string,
    enabled: PropTypes.bool,
    is_instant: PropTypes.bool,
    condition: PropTypes.string,
    filters: PropTypes.shape({})
  }).isRequired,
  meta: PropTypes.shape({}).isRequired,
  saving: PropTypes.bool.isRequired,
  editing: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onToggleEdit: PropTypes.func.isRequired
};

export default CustomRuleCard;
